/**
 * Firebase設定とサービス初期化
 * セッション管理、Analytics、認証を統合
 */

import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getAnalytics } from 'firebase/analytics';

// Firebase設定（開発環境用 - エミュレーター専用）
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "demo-api-key-for-development",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "dev-hepfive-audio-guide.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "dev-hepfive-audio-guide",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "dev-hepfive-audio-guide.appspot.com",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "303196212141",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:303196212141:web:demo-app-id",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || "G-DEMO123456",
};

// Firebase初期化（エラーハンドリング付き）
let app: any = null;
let auth: any = null;
let db: any = null;
let functions: any = null;
let analytics: any = null;

try {
  app = initializeApp(firebaseConfig);
  console.log('Firebase initialized successfully');
  console.log('Firebase config:', { projectId: firebaseConfig.projectId, apiKey: firebaseConfig.apiKey?.substring(0, 10) + '...' });

  // Initialize Firestore for analytics
  db = getFirestore(app);
  console.log('Firestore initialized:', !!db);
  console.log('Current hostname:', window.location.hostname);
  console.log('Current URL:', window.location.href);
  console.log('User agent:', navigator.userAgent);
  
  // Connect to emulator only in development with demo config
  if (import.meta.env.DEV && firebaseConfig.apiKey === "demo-api-key-for-development") {
    try {
      // Detect if this is likely a mobile device accessing via network IP
      const isLikelyMobileAccess = window.location.hostname !== 'localhost' &&
                                   window.location.hostname !== '127.0.0.1' &&
                                   /^192\.168\.|^10\.|^172\./.test(window.location.hostname);

      // Use the current hostname if accessing via network IP (for mobile devices)
      // Otherwise use localhost for desktop development
      const emulatorHost = isLikelyMobileAccess ? window.location.hostname : 'localhost';

      console.log('🔍 Attempting to connect to Firestore emulator at:', `${emulatorHost}:8080`);
      console.log('🔍 Mobile access detected:', isLikelyMobileAccess, 'Hostname:', window.location.hostname);

      connectFirestoreEmulator(db, emulatorHost, 8080);
      console.log(`✅ Connected to Firestore emulator at ${emulatorHost}:8080`);
    } catch (emulatorError) {
      console.warn('⚠️ Firestore emulator connection failed:', emulatorError instanceof Error ? emulatorError.message : String(emulatorError));
      console.warn('⚠️ Using production Firestore instead');
      // Keep db but it will use production Firestore
    }
  } else {
    console.log('🌐 Using production Firestore database');
  }
  
  // Other Firebase services (only in production with real config)
  if (firebaseConfig.apiKey !== "demo-api-key-for-development") {
    auth = getAuth(app);
    functions = getFunctions(app);
    analytics = typeof window !== 'undefined' ? getAnalytics(app) : null;
  }
  
  // Final verification
  console.log('Final Firebase state - DB:', !!db, 'Auth:', !!auth);
  
} catch (error) {
  console.error('Firebase initialization failed:', error);
  db = null;
}

export { auth, db, functions, analytics };

export default app;

// デバッグ用にグローバルアクセスを提供
if (typeof window !== 'undefined') {
  (window as any).firebaseApp = app;
  (window as any).firebaseDb = db;
  (window as any).firebaseAuth = auth;

  // Debug functions for iPhone testing
  (window as any).debugFirebase = () => {
    console.log('🔍 [DEBUG] Firebase connection status:', {
      app: !!app,
      db: !!db,
      auth: !!auth,
      hostname: window.location.hostname,
      url: window.location.href,
      userAgent: navigator.userAgent.substring(0, 100)
    });
  };

  (window as any).debugAnalytics = () => {
    console.log('🔍 [DEBUG] Analytics status:');
    // Import analytics dynamically to avoid circular dependency
    import('./analytics').then(({ analytics }) => {
      console.log('🔍 Session ID:', analytics.getSessionId());
      const storageData = analytics.getIPhoneStoredEvents();
      console.log('🔍 iPhone stored events:', storageData);
    });
  };

  console.log('🔍 [DEBUG] Global debug functions available: debugFirebase(), debugAnalytics()');
}